import {
	createStyles,
	Divider,
	Flex,
	Menu,
	UnstyledButton,
} from '@mantine/core';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import { Icon, Text } from '@repo/foundations';
import { isEmpty } from 'lib0/object';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser, type IWorkspace } from '../../../../api';
import { isImpersonating } from '../../../../utils/cache/utils';
import { useFeatureFlags } from '../../../../utils/featureFlags';
import { isSecodaCoEmail } from '../../../../utils/utils';
import { workspaceSwitchStore } from './store';
import { WorkspaceSelectorRegionSubMenu } from './WorkspaceSelectorRegionSubMenu';
import { WorkspaceSelectorSubMenu } from './WorkspaceSelectorSubMenu';

interface WorkspaceSelectorDropdownProps {
	workspaces: (IWorkspace & { is_active: boolean })[];
	setOpened: (opened: boolean) => void;
}

const useStyles = createStyles((theme) => ({
	container: {
		flexDirection: 'column',
		position: 'relative',
		width: theme.other.space[60],
		paddingTop: theme.spacing['2xs'],
		paddingBottom: theme.spacing['2xs'],
	},
	submenuDropdown: {
		width: theme.other.space[80],
		maxWidth: theme.other.space[80],
		padding: 0,
		marginLeft: 0,
	},
	menuRowButtonContainer: {
		width: '100%',
		padding: `0 ${theme.spacing['2xs']}`,
	},
	menuRowButton: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',

		width: '100%',
		height: theme.other.height.sm,
		padding: `0 ${theme.spacing['2xs']}`,
		borderRadius: theme.radius.sm,
		'&:hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
		},
		'&:active': {
			backgroundColor: theme.other.getColor('fill/transparent/active'),
		},
	},
}));

export const WorkspaceSelectorDropdown = observer(
	({ workspaces, setOpened }: WorkspaceSelectorDropdownProps) => {
		const { classes } = useStyles();
		const navigate = useNavigate();
		const { user, isAdminUser } = useAuthUser();

		const { regionSelector } = useFeatureFlags();
		const { data } = useSuspenseApiMemberEnv({});

		const handleSettingsClick = useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				e.stopPropagation();
				navigate('/settings/preferences');
				setOpened(false);
			},
			[navigate, setOpened]
		);

		return (
			<Flex className={classes.container}>
				<Flex className={classes.menuRowButtonContainer}>
					<UnstyledButton
						className={classes.menuRowButton}
						onClick={handleSettingsClick}
					>
						<Text size="sm">Settings</Text>
					</UnstyledButton>
				</Flex>

				<Flex className={classes.menuRowButtonContainer} pb="2xs">
					<UnstyledButton
						className={classes.menuRowButton}
						onClick={() => {
							navigate(`/user/${user.id}`);
						}}
					>
						<Text size="sm">Profile</Text>
					</UnstyledButton>
				</Flex>

				<Divider pb="2xs" />

				{regionSelector && !isEmpty(data?.REGION_OPTIONS ?? {}) && (
					<Menu
						classNames={{ dropdown: classes.submenuDropdown }}
						offset={{ mainAxis: 2 }}
						position="right-start"
						shadow="md"
						transitionProps={{ transition: 'fade' }}
						trigger="hover"
					>
						<Menu.Target>
							<Flex className={classes.menuRowButtonContainer}>
								<UnstyledButton className={classes.menuRowButton}>
									<Text size="sm">{data?.REGION}</Text>
									<Icon name="chevronRight" color="icon/secondary/default" />
								</UnstyledButton>
							</Flex>
						</Menu.Target>
						<Menu.Dropdown>
							<WorkspaceSelectorRegionSubMenu />
						</Menu.Dropdown>
					</Menu>
				)}

				{(workspaces.length > 1 ||
					isAdminUser ||
					workspaceSwitchStore.account?.is_superuser ||
					isSecodaCoEmail(user?.email)) &&
					!isImpersonating() && (
						<Menu
							classNames={{ dropdown: classes.submenuDropdown }}
							offset={{ mainAxis: 2 }}
							position="right-start"
							shadow="md"
							transitionProps={{ transition: 'fade' }}
							trigger="hover"
						>
							<Menu.Target>
								<Flex className={classes.menuRowButtonContainer}>
									<UnstyledButton className={classes.menuRowButton}>
										<Text size="sm">Switch workspace</Text>
										<Icon name="chevronRight" color="icon/secondary/default" />
									</UnstyledButton>
								</Flex>
							</Menu.Target>
							<Menu.Dropdown>
								<WorkspaceSelectorSubMenu workspaces={workspaces} />
							</Menu.Dropdown>
						</Menu>
					)}

				<Flex className={classes.menuRowButtonContainer}>
					<UnstyledButton
						className={classes.menuRowButton}
						onClick={() => {
							navigate('/auth/logout');
						}}
					>
						<Text size="sm">Log out</Text>
					</UnstyledButton>
				</Flex>
			</Flex>
		);
	}
);
